import { Box, CircularProgress, Link, Typography } from '@mui/material';
import { FC, useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { EasTableWithoutFilter } from '../../../../../ui/EasTableWithoutFilter';
import { ItemWithId, THandleAction, TTableStructure } from '../../../../../ui/EasTable/types';
import { TPageResponse } from '../../../../../redux/types';
import { getEmInvoiceListRequest } from '../../../../../redux/reducers/emManagement/reducer';
import {
	getEmByIdFull,
	getEmInvoiceList,
	getEmInvoiceListLoading,
	getEnumsServiceType,
	getEnumsServiceTypeLoading,
} from '../../../../../redux/reducers/emManagement/selectors';
import { TEmInvoiceListItem } from '../../../../../redux/reducers/emManagement/types';
import { tableStructure } from './tableStructure';
import { OpenInNew } from '@mui/icons-material';
import { emBoxesStructure } from '../../emBoxesStructure';

export const LastInvoices: FC = () => {
	const dispatch = useDispatch();
	const emDataFull = useSelector(getEmByIdFull);
	const emInvoiceList = useSelector(getEmInvoiceList);
	const emInvoiceListLoading = useSelector(getEmInvoiceListLoading);
	const enumsServiceType = useSelector(getEnumsServiceType);
	const enumsServiceTypeLoading = useSelector(getEnumsServiceTypeLoading);
	const lastInvoicesLink = emBoxesStructure(emDataFull).find((box) => box.id === 'last-invoices')?.link;

	useEffect(() => {
		if (emDataFull?.id) {
			dispatch(getEmInvoiceListRequest({ emId: emDataFull.id, page: { size: 3, from: 0 } }));
		}
	}, [dispatch, emDataFull?.id]);

	// Transform API response to match EasTableWithoutFilter expected format
	const tableData: TPageResponse<TEmInvoiceListItem & ItemWithId> | null = !emInvoiceList
		? null
		: {
				data: emInvoiceList.rows.map((row) => ({
					...row,
					id: row.invoice_id,
					service_type:
						enumsServiceType?.find((service) => service.value === row.service_type)?.displayName ??
						row.service_type,
				})),
				pageSize: emInvoiceList.page.size,
				pageNumber: emInvoiceList.page.current,
				totalCount: emInvoiceList.page.total,
			};

	const invoiceTableStructure = tableStructure as unknown as TTableStructure<
		keyof (TEmInvoiceListItem & ItemWithId),
		TEmInvoiceListItem & ItemWithId
	>[];

	const handleAction: THandleAction = useCallback(() => {
		// No actions for this table
	}, []);

	return (
		<EasTableWithoutFilter<TEmInvoiceListItem & ItemWithId>
			tableStructure={invoiceTableStructure}
			items={tableData}
			itemsIsLoading={emInvoiceListLoading || enumsServiceTypeLoading}
			handleAction={handleAction}
			skeletonRows={[1]}
		>
			<Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
				<Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 1 }}>
					<Typography variant="body1" color="primary" fontWeight="bold">
						Last Invoices
					</Typography>
					<Link
						href={lastInvoicesLink?.url}
						target="_blank"
						sx={{
							display: 'flex',
							flexDirection: 'row',
							alignItems: 'center',
							gap: 0.5,
							textDecoration: 'none',
							m:0, p:0
						}}
					>
						<Typography
							variant="body1"
							color="primary"
							fontWeight="bold"
							sx={{ fontSize: '0.8rem', m:0, p:0 }}
						>
							{lastInvoicesLink?.label}
						</Typography>
						<OpenInNew sx={{ fontSize: '0.7rem', p: 0, m: 0 }} />
					</Link>
				</Box>
				{emInvoiceListLoading && <CircularProgress size={20} />}
			</Box>
		</EasTableWithoutFilter>
	);
};
